import SlidingIndicator from "./SlidingIndicator";
import ActionButton, { useActionStatus } from "./ActionButton";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  EstimateCostParams,
  ProviderParameterValue,
  VideoFrameImageInput,
  VideoFrameType,
  VideoGenerationMode,
  VideoModelMeta,
  VideoReferenceImageInput,
  fetchVideoModels,
  estimateCost,
} from "../firebase/video";
import {
  Clock3,
  DollarSign,
  Film,
  Hash,
  ImagePlus,
  Images,
  Loader2,
  Settings2,
  Sparkles,
  Upload,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";

type UploadedImage = {
  id: string;
  url: string;
  previewUrl: string;
  name: string;
};

interface VideoConfigFormProps {
  projectId: string | null;
  projectClipCount: number;
  onSubmit: (params: {
    projectId: string | null;
    sceneTitle: string | null;
    sceneIndex: number;
    model: string;
    prompt: string;
    generationMode: VideoGenerationMode;
    duration: number;
    resolution: string | null;
    aspectRatio: string | null;
    size: string | null;
    generateAudio: boolean;
    seed: number | null;
    frameImages: VideoFrameImageInput[];
    inputReferences: VideoReferenceImageInput[];
    providerParameters: Record<string, ProviderParameterValue>;
  }) => Promise<void>;
  chatCredits: number;
  freeAccess?: boolean;
  submitting: boolean;
  activeJobCount: number;
  retryParams?: {
    model: string;
    prompt: string;
    generationMode?: VideoGenerationMode;
    duration: number | null;
    resolution: string | null;
    aspectRatio: string | null;
    size?: string | null;
    generateAudio: boolean;
    seed?: number | null;
    providerParameters?: Record<string, ProviderParameterValue>;
    sceneTitle?: string | null;
  } | null;
}

const MAX_IMAGE_DATA_URL_BYTES = 3 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1920;
const MAX_REFERENCES = 4;

const frameLabels: Record<VideoFrameType, string> = {
  first_frame: "Start frame",
  last_frame: "End frame",
};

const modeLabels: Record<VideoGenerationMode, string> = {
  text_to_video: "Text",
  frame_to_video: "Frames",
  reference_to_video: "References",
};

const booleanProviderKeys = new Set([
  "enhancePrompt",
  "enable_prompt_expansion",
  "prompt_extend",
  "prompt_optimizer",
  "fast_pretreatment",
  "watermark",
  "audio",
]);

const numberProviderKeys = new Set(["cfg_scale", "conditioningScale"]);

function supportsReferences(model: VideoModelMeta | null): boolean {
  if (!model) return false;
  const text = `${model.id} ${model.description ?? ""} ${(
    model.allowed_passthrough_parameters ?? []
  ).join(" ")}`.toLowerCase();
  return (
    text.includes("reference") ||
    text.includes("input_references") ||
    text.includes("reference-to-video") ||
    text.includes("reference images") ||
    text.includes("images")
  );
}

function uniqueNumbers(values: number[] | null | undefined): number[] {
  return Array.from(new Set(values?.filter((value) => Number.isFinite(value)) ?? []))
    .sort((a, b) => a - b);
}

function uniqueStrings(values: string[] | null | undefined): string[] {
  return Array.from(new Set(values?.filter(Boolean) ?? []));
}

function resolutionFromSize(size: string | null): string | null {
  if (!size) return null;
  const [width, height] = size.split("x").map((value) => Number(value));
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  const longEdge = Math.max(width, height);
  const shortEdge = Math.min(width, height);
  if (longEdge >= 3840 || shortEdge >= 2160) return "4K";
  if (longEdge >= 1920 || shortEdge >= 1080) return "1080p";
  if (longEdge >= 1280 || shortEdge >= 720) return "720p";
  if (longEdge >= 854 || shortEdge >= 480) return "480p";
  return null;
}

function aspectRatioFromSize(size: string | null): string | null {
  if (!size) return null;
  const [width, height] = size.split("x").map((value) => Number(value));
  if (!Number.isFinite(width) || !Number.isFinite(height) || height === 0) {
    return null;
  }
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  return `${width / divisor}:${height / divisor}`;
}

function createUploadId(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function fileToCompressedDataUrl(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please upload an image file.");
  }

  const sourceUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read image."));
    reader.readAsDataURL(file);
  });

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode image."));
    img.src = sourceUrl;
  });

  const canvas = document.createElement("canvas");
  let { width, height } = image;
  if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
    const ratio = Math.min(
      MAX_IMAGE_DIMENSION / width,
      MAX_IMAGE_DIMENSION / height,
    );
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not prepare image.");
  ctx.drawImage(image, 0, 0, width, height);

  let quality = 0.86;
  let dataUrl = canvas.toDataURL("image/jpeg", quality);
  while (
    new Blob([dataUrl]).size > MAX_IMAGE_DATA_URL_BYTES &&
    quality > 0.42
  ) {
    quality -= 0.08;
    dataUrl = canvas.toDataURL("image/jpeg", quality);
  }
  if (new Blob([dataUrl]).size > MAX_IMAGE_DATA_URL_BYTES) {
    throw new Error("Image is still too large after compression.");
  }
  return dataUrl;
}

const VideoConfigForm: React.FC<VideoConfigFormProps> = ({
  projectId,
  projectClipCount,
  onSubmit,
  chatCredits,
  freeAccess = false,
  submitting,
  activeJobCount,
  retryParams,
}) => {
  const [models, setModels] = useState<VideoModelMeta[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState("");
  const [prompt, setPrompt] = useState("");
  const [sceneTitle, setSceneTitle] = useState("");
  const [generationMode, setGenerationMode] =
    useState<VideoGenerationMode>("text_to_video");
  const [duration, setDuration] = useState(4);
  const [resolution, setResolution] = useState("720p");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [size, setSize] = useState<string | null>(null);
  const [useExactSize, setUseExactSize] = useState(true);
  const [generateAudio, setGenerateAudio] = useState(true);
  const [seedEnabled, setSeedEnabled] = useState(false);
  const [seed, setSeed] = useState("");
  const [frameImages, setFrameImages] = useState<
    Partial<Record<VideoFrameType, UploadedImage>>
  >({});
  const [inputReferences, setInputReferences] = useState<UploadedImage[]>([]);
  const [providerParameters, setProviderParameters] = useState<
    Record<string, ProviderParameterValue>
  >({});
  const [estimatedCostWithMarkup, setEstimatedCostWithMarkup] = useState<
    number | null
  >(null);
  const [costLoading, setCostLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [estimateAttempt, setEstimateAttempt] = useState(0);
  const [formError, setFormError] = useState("");
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const referenceInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    setModelsLoading(true);
    setModelsError("");
    fetchVideoModels()
      .then((videoModels) => {
        if (!active) return;
        setModels(videoModels);
        if (videoModels.length > 0) {
          setSelectedModel((current) => current || videoModels[0].id);
        }
      })
      .catch(() => { if (active) setModelsError("We couldn’t load the video models. Your draft is still here."); })
      .finally(() => { if (active) setModelsLoading(false); });
    return () => { active = false; };
  }, [loadAttempt]);

  const currentModel = useMemo(
    () => models.find((model) => model.id === selectedModel) ?? null,
    [models, selectedModel],
  );

  const availableDurations = useMemo(() => {
    const values = uniqueNumbers(currentModel?.supported_durations);
    return values.length ? values : [4, 6, 8];
  }, [currentModel]);

  const availableResolutions = useMemo(() => {
    const values = uniqueStrings(currentModel?.supported_resolutions);
    return values.length ? values : ["720p"];
  }, [currentModel]);

  const availableAspectRatios = useMemo(() => {
    const values = uniqueStrings(currentModel?.supported_aspect_ratios);
    return values.length ? values : ["16:9"];
  }, [currentModel]);

  const availableSizes = useMemo(
    () => uniqueStrings(currentModel?.supported_sizes),
    [currentModel],
  );

  const supportedFrameTypes = useMemo(
    () => uniqueStrings(currentModel?.supported_frame_images) as VideoFrameType[],
    [currentModel],
  );

  const supportsFrameMode = supportedFrameTypes.length > 0;
  const supportsReferenceMode = supportsReferences(currentModel);
  const supportsAudio = currentModel?.generate_audio === true;
  const supportsSeed = currentModel?.seed === true;
  const allowedProviderParameters = useMemo(
    () => uniqueStrings(currentModel?.allowed_passthrough_parameters),
    [currentModel],
  );

  const effectiveSize = useExactSize && availableSizes.length ? size : null;
  const effectiveResolution =
    effectiveSize ? resolutionFromSize(effectiveSize) ?? resolution : resolution;
  const effectiveAspectRatio =
    effectiveSize ? aspectRatioFromSize(effectiveSize) ?? aspectRatio : aspectRatio;

  useEffect(() => {
    if (!currentModel) return;
    if (!availableDurations.includes(duration)) {
      setDuration(availableDurations[0]);
    }
    if (!availableResolutions.includes(resolution)) {
      setResolution(availableResolutions[0]);
    }
    if (!availableAspectRatios.includes(aspectRatio)) {
      setAspectRatio(availableAspectRatios[0]);
    }
    if (availableSizes.length && (!size || !availableSizes.includes(size))) {
      setSize(availableSizes[0]);
    }
    if (!availableSizes.length) {
      setUseExactSize(false);
      setSize(null);
    }
    if (generationMode === "frame_to_video" && !supportsFrameMode) {
      setGenerationMode("text_to_video");
    }
    if (generationMode === "reference_to_video" && !supportsReferenceMode) {
      setGenerationMode("text_to_video");
    }
    if (!supportsAudio) {
      setGenerateAudio(false);
    }
    if (!supportsSeed) {
      setSeedEnabled(false);
      setSeed("");
    }
    setProviderParameters((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([key]) =>
          allowedProviderParameters.includes(key),
        ),
      ),
    );
  }, [
    currentModel,
    availableDurations,
    availableResolutions,
    availableAspectRatios,
    availableSizes,
    size,
    duration,
    resolution,
    aspectRatio,
    generationMode,
    supportsFrameMode,
    supportsReferenceMode,
    supportsAudio,
    supportsSeed,
    allowedProviderParameters,
  ]);

  useEffect(() => {
    if (!currentModel) return;
    setGenerateAudio(currentModel.generate_audio === true);
    setSeedEnabled(false);
    setSeed("");
  }, [currentModel]);

  useEffect(() => {
    if (retryParams) {
      setSelectedModel(retryParams.model);
      setPrompt(retryParams.prompt);
      setSceneTitle(retryParams.sceneTitle ?? "");
      setGenerationMode(retryParams.generationMode ?? "text_to_video");
      if (retryParams.duration) setDuration(retryParams.duration);
      if (retryParams.resolution) setResolution(retryParams.resolution);
      if (retryParams.aspectRatio) setAspectRatio(retryParams.aspectRatio);
      if (retryParams.size) {
        setSize(retryParams.size);
        setUseExactSize(true);
      }
      setGenerateAudio(retryParams.generateAudio);
      if (retryParams.seed != null) {
        setSeedEnabled(true);
        setSeed(String(retryParams.seed));
      }
      setProviderParameters(retryParams.providerParameters ?? {});
    }
  }, [retryParams]);

  useEffect(() => {
    setSceneTitle((current) => current || `Shot ${projectClipCount + 1}`);
  }, [projectClipCount, projectId]);

  useEffect(() => {
    if (!selectedModel) return;
    let active = true;
    setEstimatedCostWithMarkup(null);
    setCostLoading(true);
    const timeout = setTimeout(() => {
      const estimateParams: EstimateCostParams = {
        model: selectedModel,
        generationMode,
        duration,
        resolution: effectiveResolution,
        aspectRatio: effectiveAspectRatio,
        size: effectiveSize,
        generateAudio,
        frameImages:
          generationMode === "frame_to_video"
            ? Object.entries(frameImages).map(([frameType, image]) => ({
                frameType: frameType as VideoFrameType,
                url: image?.url ?? "",
              }))
            : [],
        inputReferences:
          generationMode === "reference_to_video"
            ? inputReferences.map((image) => ({ url: image.url }))
            : [],
      };
      estimateCost(estimateParams)
        .then((data) => {
          if (active) setEstimatedCostWithMarkup(data.estimatedCostWithMarkup);
        })
        .catch(() => {
          if (active) setEstimatedCostWithMarkup(null);
        })
        .finally(() => { if (active) setCostLoading(false); });
    }, 300);
    return () => { active = false; clearTimeout(timeout); };
  }, [
    estimateAttempt,
    selectedModel,
    generationMode,
    duration,
    effectiveResolution,
    effectiveAspectRatio,
    effectiveSize,
    generateAudio,
    frameImages,
    inputReferences,
  ]);

  const prepareUpload = useCallback(async (file: File): Promise<UploadedImage> => {
    const dataUrl = await fileToCompressedDataUrl(file);
    return {
      id: createUploadId(),
      url: dataUrl,
      previewUrl: dataUrl,
      name: file.name,
    };
  }, []);

  const handleFrameFile = useCallback(
    async (frameType: VideoFrameType, file: File | undefined) => {
      if (!file) return;
      setUploadingImage(frameType);
      try {
        const image = await prepareUpload(file);
        setFrameImages((current) => ({ ...current, [frameType]: image }));
      } catch (err: any) {
        setFormError(err.message || "That image couldn’t be attached. Please try another image.");
      } finally {
        setUploadingImage(null);
      }
    },
    [prepareUpload],
  );

  const handleReferenceFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      const remaining = MAX_REFERENCES - inputReferences.length;
      const nextFiles = Array.from(files).slice(0, Math.max(remaining, 0));
      if (!nextFiles.length) return;
      setUploadingImage("references");
      try {
        const uploads = await Promise.all(nextFiles.map(prepareUpload));
        setInputReferences((current) => [...current, ...uploads]);
      } catch (err: any) {
        setFormError(err.message || "That image couldn’t be attached. Please try another image.");
      } finally {
        setUploadingImage(null);
        if (referenceInputRef.current) referenceInputRef.current.value = "";
      }
    },
    [inputReferences.length, prepareUpload],
  );

  const removeFrameImage = (frameType: VideoFrameType) => {
    setFrameImages((current) => {
      const next = { ...current };
      delete next[frameType];
      return next;
    });
  };

  const removeReference = (id: string) => {
    setInputReferences((current) => current.filter((image) => image.id !== id));
  };

  const handleProviderChange = (key: string, value: ProviderParameterValue) => {
    setProviderParameters((current) => ({ ...current, [key]: value }));
  };

  const selectedSeed =
    seedEnabled && seed.trim() ? Number(seed.trim()) : null;
  const frameImageList = Object.entries(frameImages)
    .filter((entry): entry is [VideoFrameType, UploadedImage] => Boolean(entry[1]))
    .map(([frameType, image]) => ({ frameType, url: image.url }));
  const referenceList = inputReferences.map((image) => ({ url: image.url }));
  const modeNeedsFrame =
    generationMode === "frame_to_video" && frameImageList.length === 0;
  const modeNeedsReference =
    generationMode === "reference_to_video" && referenceList.length === 0;

  const canSubmit =
    !submitting &&
    !uploadingImage &&
    (freeAccess || (!costLoading && estimatedCostWithMarkup != null)) &&
    prompt.trim().length > 0 &&
    selectedModel.length > 0 &&
    activeJobCount < 3 &&
    !modeNeedsFrame &&
    !modeNeedsReference &&
    (!seedEnabled || (selectedSeed != null && Number.isFinite(selectedSeed))) &&
    (freeAccess ||
      estimatedCostWithMarkup == null ||
      chatCredits >= estimatedCostWithMarkup);

  const submitAction = useActionStatus();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setFormError("");
    try {
    await onSubmit({
      projectId,
      sceneTitle: sceneTitle.trim() || null,
      sceneIndex: projectClipCount + 1,
      model: selectedModel,
      prompt: prompt.trim(),
      generationMode,
      duration,
      resolution: effectiveResolution,
      aspectRatio: effectiveAspectRatio,
      size: effectiveSize,
      generateAudio,
      seed: selectedSeed,
      frameImages: generationMode === "frame_to_video" ? frameImageList : [],
      inputReferences:
        generationMode === "reference_to_video" ? referenceList : [],
      providerParameters,
    });
    setPrompt("");
    setFrameImages({});
    setInputReferences([]);
    setSceneTitle(`Shot ${projectClipCount + 2}`);
    submitAction.settle("success");
    } catch (err) {
      submitAction.settle("error");
      setFormError("Your clip couldn’t be submitted. Your prompt and settings are saved here so you can try again.");
    }
  };

  if (modelsLoading) {
    return (
      <div className="studio-form-loading" role="status" aria-label="Loading video models">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <p>Getting your studio ready…</p>
      </div>
    );
  }
  if (modelsError || models.length === 0) {
    return <div className="studio-form-loading"><Film size={28} className="text-accent" /><h2>Let’s get your studio connected.</h2><p role="alert">{modelsError || "No video models are available right now."}</p><button type="button" className="button-primary" onClick={() => setLoadAttempt(v => v + 1)}>Try again</button></div>;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="video-config-form"
    >
      <div className="px-4 pt-4 pb-3 border-b border-line">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-accent-soft rounded-lg">
            <Film size={18} className="text-accent" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-content">
              Generate Clip
            </h2>
            <p className="text-xs text-muted">
              {currentModel?.name || currentModel?.id || "OpenRouter video"}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <label htmlFor="video-model" className="block text-sm font-medium text-content mb-1">
            Model
          </label>
          <select
            id="video-model"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-line bg-subtle text-sm focus:outline-none focus:ring-2 focus:ring-focus"
          >
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name || model.id}
              </option>
            ))}
          </select>
        </div>

        <div className="relative grid grid-cols-3 gap-1 rounded-lg bg-raised p-1" role="group" aria-label="Clip input mode">
          <SlidingIndicator selector='[aria-pressed="true"]' activeKey={generationMode} className="seg-indicator-paper" />
          {(["text_to_video", "frame_to_video", "reference_to_video"] as const)
            .filter(
              (mode) =>
                mode === "text_to_video" ||
                (mode === "frame_to_video" && supportsFrameMode) ||
                (mode === "reference_to_video" && supportsReferenceMode),
            )
            .map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={generationMode === mode}
                onClick={() => setGenerationMode(mode)}
                className={`relative z-[1] px-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 focus-visible:outline-offset-1 ${
                  generationMode === mode
                    ? "text-content"
                    : "text-muted hover:text-content"
                }`}
              >
                {modeLabels[mode]}
              </button>
            ))}
        </div>

        <div>
          <label htmlFor="video-scene" className="block text-sm font-medium text-content mb-1">
            Scene
          </label>
          <input
            id="video-scene"
            value={sceneTitle}
            onChange={(e) => setSceneTitle(e.target.value)}
            placeholder={`Shot ${projectClipCount + 1}`}
            className="w-full px-3 py-2 rounded-lg border border-line bg-subtle text-sm focus:outline-none focus:ring-2 focus:ring-focus"
          />
        </div>

        <div>
          <label htmlFor="video-prompt" className="block text-sm font-medium text-content mb-1">
            Prompt
          </label>
          <textarea
            id="video-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Motion, subject, camera, lighting, style..."
            rows={4}
            className="w-full px-3 py-2 rounded-lg border border-line bg-subtle text-sm resize-none focus:outline-none focus:ring-2 focus:ring-focus"
          />
          <div className="text-right text-xs text-muted mt-0.5">
            {prompt.length} characters
          </div>
        </div>

        {generationMode === "frame_to_video" && (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-sm font-medium text-content">
              <Images size={15} className="text-muted" />
              Frames
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
              {supportedFrameTypes.map((frameType) => {
                const image = frameImages[frameType];
                return (
                  <div
                    key={frameType}
                    className="rounded-lg border border-line bg-subtle p-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-muted">
                        {frameLabels[frameType]}
                      </span>
                      {image && (
                        <button
                          type="button"
                          onClick={() => removeFrameImage(frameType)}
                          className="p-1 rounded-md text-muted hover:bg-danger-soft hover:text-danger"
                          title="Remove"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {image ? (
                      <img
                        src={image.previewUrl}
                        alt={frameLabels[frameType]}
                        className="mt-2 w-full aspect-video object-cover rounded-md border border-line"
                      />
                    ) : (
                      <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-control bg-surface px-3 py-4 text-xs font-medium text-muted hover:border-accent-line hover:text-accent">
                        {uploadingImage === frameType ? (
                          <Loader2 size={15} className="animate-spin" />
                        ) : (
                          <Upload size={15} />
                        )}
                        Upload
                        <input
                          type="file" aria-label={`${frameLabels[frameType]} image`}
                          accept="image/*"
                          className="sr-only"
                          onChange={(e) =>
                            handleFrameFile(frameType, e.target.files?.[0])
                          }
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {generationMode === "reference_to_video" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm font-medium text-content">
                <ImagePlus size={15} className="text-muted" />
                References
              </div>
              <button
                type="button"
                onClick={() => referenceInputRef.current?.click()}
                disabled={inputReferences.length >= MAX_REFERENCES}
                className="inline-flex items-center gap-1.5 rounded-md border border-line px-2 py-1 text-xs font-medium text-muted hover:bg-subtle disabled:opacity-50"
              >
                {uploadingImage === "references" ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Upload size={13} />
                )}
                Add
              </button>
              <input
                ref={referenceInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleReferenceFiles(e.target.files)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {inputReferences.map((image) => (
                <div key={image.id} className="relative">
                  <img
                    src={image.previewUrl}
                    alt={image.name}
                    className="w-full aspect-video object-cover rounded-md border border-line"
                  />
                  <button
                    type="button"
                    onClick={() => removeReference(image.id)}
                    className="absolute top-1 right-1 p-1 rounded-md bg-surface/90 text-muted hover:text-danger"
                    title="Remove"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
              {inputReferences.length === 0 && (
                <button
                  type="button"
                  onClick={() => referenceInputRef.current?.click()}
                  className="col-span-2 flex items-center justify-center gap-2 rounded-lg border border-dashed border-control bg-subtle px-3 py-5 text-xs font-medium text-muted hover:border-accent-line hover:text-accent"
                >
                  <ImagePlus size={15} />
                  Add reference photos
                </button>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label htmlFor="video-duration" className="flex items-center gap-1 text-xs font-medium text-muted mb-1">
              <Clock3 size={13} />
              Duration
            </label>
            <select
              id="video-duration"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full px-2 py-1.5 rounded-lg border border-line bg-subtle text-sm focus:outline-none focus:ring-2 focus:ring-focus"
            >
              {availableDurations.map((value) => (
                <option key={value} value={value}>
                  {value}s
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="video-resolution" className="block text-xs font-medium text-muted mb-1">
              Resolution
            </label>
            <select
              id="video-resolution"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              disabled={Boolean(effectiveSize)}
              className="w-full px-2 py-1.5 rounded-lg border border-line bg-subtle text-sm focus:outline-none focus:ring-2 focus:ring-focus disabled:text-muted"
            >
              {availableResolutions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="video-ratio" className="block text-xs font-medium text-muted mb-1">
              Ratio
            </label>
            <select
              id="video-ratio"
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value)}
              disabled={Boolean(effectiveSize)}
              className="w-full px-2 py-1.5 rounded-lg border border-line bg-subtle text-sm focus:outline-none focus:ring-2 focus:ring-focus disabled:text-muted"
            >
              {availableAspectRatios.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>

        {availableSizes.length > 0 && (
          <div className="rounded-lg border border-line bg-subtle p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-content">
                Exact size
              </span>
              <button
                type="button"
                role="switch" aria-checked={useExactSize} aria-label="Use exact size"
                onClick={() => setUseExactSize((current) => !current)}
                className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                  useExactSize ? "bg-action" : "bg-control"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-on-solid shadow-sm transition-transform ${
                    useExactSize ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            {useExactSize && (
              <select
                aria-label="Exact video size"
                value={size ?? ""}
                onChange={(e) => setSize(e.target.value)}
                className="w-full px-2 py-1.5 rounded-lg border border-line bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-focus"
              >
                {availableSizes.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="space-y-2">
          {supportsAudio && (
            <div className="flex items-center justify-between rounded-lg border border-line bg-subtle p-3">
              <div className="flex items-center gap-2 text-sm text-content">
                {generateAudio ? (
                  <Volume2 size={16} className="text-accent" />
                ) : (
                  <VolumeX size={16} className="text-muted" />
                )}
                Audio
              </div>
              <button
                type="button"
                role="switch" aria-checked={generateAudio} aria-label="Generate audio"
                onClick={() => setGenerateAudio((current) => !current)}
                className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                  generateAudio ? "bg-action" : "bg-control"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-on-solid shadow-sm transition-transform ${
                    generateAudio ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          )}

          {supportsSeed && (
            <div className="rounded-lg border border-line bg-subtle p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-content">
                  <Hash size={16} className="text-muted" />
                  Seed
                </div>
                <button
                  type="button"
                  role="switch" aria-checked={seedEnabled} aria-label="Use seed"
                onClick={() => setSeedEnabled((current) => !current)}
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                    seedEnabled ? "bg-action" : "bg-control"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-on-solid shadow-sm transition-transform ${
                      seedEnabled ? "translate-x-5" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
              {seedEnabled && (
                <input
                  type="number" aria-label="Seed value"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                  placeholder="12345"
                  className="w-full px-2 py-1.5 rounded-lg border border-line bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-focus"
                />
              )}
            </div>
          )}
        </div>

        {allowedProviderParameters.length > 0 && (
          <details className="rounded-lg border border-line bg-surface">
            <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium text-content">
              <Settings2 size={15} className="text-muted" />
              Provider parameters
            </summary>
            <div className="border-t border-line p-3 space-y-3">
              {allowedProviderParameters.map((key) => {
                const value = providerParameters[key];
                if (booleanProviderKeys.has(key)) {
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted">
                        {key}
                      </span>
                      <button
                        type="button"
                        role="switch" aria-checked={value === true} aria-label={key}
                        onClick={() => handleProviderChange(key, value !== true)}
                        className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                          value === true ? "bg-action" : "bg-control"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-on-solid shadow-sm transition-transform ${
                            value === true ? "translate-x-5" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  );
                }
                return (
                  <div key={key}>
                    <label htmlFor={`provider-${key}`} className="block text-xs font-medium text-muted mb-1">
                      {key}
                    </label>
                    {key.toLowerCase().includes("negative") ? (
                      <textarea
                        id={`provider-${key}`}
                        value={String(value ?? "")}
                        onChange={(e) =>
                          handleProviderChange(key, e.target.value || null)
                        }
                        rows={2}
                        className="w-full px-2 py-1.5 rounded-lg border border-line bg-subtle text-sm resize-none focus:outline-none focus:ring-2 focus:ring-focus"
                      />
                    ) : (
                      <input
                        type={numberProviderKeys.has(key) ? "number" : "text"}
                        id={`provider-${key}`}
                        value={String(value ?? "")}
                        onChange={(e) =>
                          handleProviderChange(
                            key,
                            numberProviderKeys.has(key)
                              ? e.target.value === ""
                                ? null
                                : Number(e.target.value)
                              : e.target.value || null,
                          )
                        }
                        className="w-full px-2 py-1.5 rounded-lg border border-line bg-subtle text-sm focus:outline-none focus:ring-2 focus:ring-focus"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </details>
        )}

        <div className="rounded-lg border border-line bg-subtle p-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted flex items-center gap-1">
              <DollarSign size={14} />
              Estimate
            </span>
            <span className="font-medium text-content">
              {freeAccess ? (
                "Free for you"
              ) : costLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : estimatedCostWithMarkup != null ? (
                `$${estimatedCostWithMarkup.toFixed(2)}`
              ) : (
                "Unavailable"
              )}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted">Balance</span>
            <span
              className={`font-medium ${
                !freeAccess && chatCredits < (estimatedCostWithMarkup ?? 0)
                  ? "text-danger"
                  : "text-success"
              }`}
            >
              {freeAccess ? "Free access" : `$${chatCredits.toFixed(2)}`}
            </span>
          </div>
          {estimatedCostWithMarkup != null &&
            !freeAccess &&
            chatCredits < estimatedCostWithMarkup && (
              <p className="text-xs text-danger">
                Add credits before generating this clip.
              </p>
            )}
        </div>

        {formError && <p role="alert" className="rounded-xl border border-danger-line bg-danger-soft p-3 text-sm leading-6 text-danger">{formError}</p>}
        {!freeAccess && !costLoading && estimatedCostWithMarkup == null && <div className="rounded-xl border border-warning-line bg-warning-soft p-3 text-xs leading-6 text-warning">We couldn’t confirm the cost. <button type="button" className="font-semibold underline" onClick={() => setEstimateAttempt(v => v + 1)}>Retry estimate</button></div>}
        <ActionButton
          type="submit"
          disabled={!canSubmit}
          status={submitting ? "pending" : submitAction.status}
          icon={<Sparkles size={16} />}
          label="Generate Clip"
          pendingLabel="Submitting…"
          successLabel="Added to storyboard"
          errorLabel="Not submitted"
          className="w-full px-4 py-3 rounded-md bg-action hover:bg-action-hover active:translate-y-px text-on-solid font-semibold text-sm shadow-xs disabled:opacity-50 disabled:cursor-not-allowed transition-[background-color,transform] duration-150"
        />

        {activeJobCount >= 3 && (
          <p className="text-xs text-warning text-center">
            Maximum 3 concurrent jobs.
          </p>
        )}
      </div>
    </form>
  );
};

export default VideoConfigForm;
